import { useEffect, useRef, useState } from "react";
import { Bell } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { NotificationItem } from "../hooks/useNotifications";
import { useNotificationsContext } from "../context/NotificationsContext";

function timeAgo(iso: string) {
  const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (diff < 60)    return "À l'instant";
  if (diff < 3600)  return `Il y a ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `Il y a ${Math.floor(diff / 3600)} h`;
  return new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "short" });
}

const KIND_ICON: Record<string, string> = {
  new_analysis:  "📊",
  coach_comment: "💬",
};

function notifRoute(n: NotificationItem): string | null {
  if (n.kind === "new_analysis")  return "/admin?tab=analyses";
  if (n.kind === "coach_comment") return "/exercices";
  return null;
}

export default function NotificationBell() {
  const { notifications, unreadCount, markAllRead, markRead } = useNotificationsContext();
  const [open, setOpen] = useState(false);
  const ref             = useRef<HTMLDivElement>(null);
  const navigate        = useNavigate();

  useEffect(() => {
    const handler = (e: Event) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    document.addEventListener("touchstart", handler);
    return () => {
      document.removeEventListener("mousedown", handler);
      document.removeEventListener("touchstart", handler);
    };
  }, []);

  const handleClick = (n: NotificationItem) => {
    const route = notifRoute(n);
    if (route) {
      // Marquer lu seulement si pas de badge associé côté client
      // (coach_comment = badge sur l'exercice, on le laisse jusqu'à lecture réelle)
      if (n.kind !== "coach_comment" && !n.read_at) markRead(n.id);
      navigate(route);
    } else if (!n.read_at) {
      markRead(n.id);
    }
    setOpen(false);
  };

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className="relative flex h-9 w-9 items-center justify-center rounded-full border border-zinc-700 bg-zinc-900 transition hover:border-yellow-400/40"
        aria-label="Notifications"
      >
        <Bell className="h-4 w-4 text-zinc-300" />
        {unreadCount > 0 && (
          <span className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-yellow-400 text-[10px] font-bold text-zinc-900">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="fixed left-3 right-3 top-16 z-50 overflow-hidden rounded-2xl border border-zinc-800 bg-zinc-950 shadow-2xl md:absolute md:left-auto md:right-0 md:top-11 md:w-80 md:max-w-[calc(100vw-24px)]">
          <div className="flex items-center justify-between border-b border-zinc-800 px-4 py-3">
            <span className="text-sm font-semibold text-white">Notifications</span>
            {unreadCount > 0 && (
              <button
                onClick={markAllRead}
                className="text-xs text-zinc-400 hover:text-yellow-300"
              >
                Tout marquer lu
              </button>
            )}
          </div>

          <ul className="max-h-96 overflow-y-auto">
            {notifications.length === 0 && (
              <li className="px-4 py-6 text-center text-sm text-zinc-500">
                Aucune notification
              </li>
            )}
            {notifications.map((n) => {
              const hasRoute = !!notifRoute(n);
              return (
                <li key={n.id}>
                  <button
                    onClick={() => handleClick(n)}
                    className={`flex w-full gap-3 px-4 py-3 text-left transition hover:bg-zinc-900 ${
                      !n.read_at ? "bg-zinc-900/60" : ""
                    } ${hasRoute ? "cursor-pointer" : ""}`}
                  >
                    <span className="mt-0.5 shrink-0 text-base">
                      {KIND_ICON[n.kind] ?? "🔔"}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-start justify-between gap-2">
                        <p className={`text-sm ${!n.read_at ? "font-semibold text-white" : "text-zinc-300"}`}>
                          {n.title}
                        </p>
                        {!n.read_at && (
                          <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-yellow-400" />
                        )}
                      </div>
                      {n.body && (
                        <p className="text-xs text-zinc-500">{n.body}</p>
                      )}
                      <div className="mt-1 flex items-center gap-2">
                        <p className="text-xs text-zinc-600">{timeAgo(n.created_at)}</p>
                        {hasRoute && (
                          <span className="text-xs text-yellow-400/60">→ Voir</span>
                        )}
                      </div>
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
